import {
	ChevronLeftIcon,
	ChevronRightIcon,
	MapPinIcon,
	PencilIcon,
	PlayIcon,
	TrashIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import { format, parseISO } from "date-fns";
import { enUS, es, fr, he, ru } from "date-fns/locale";
import { motion, type PanInfo } from "motion/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useChatActions } from "../../../contexts/chat-actions-context";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import {
	TRACK_EVENT_ASK_EVE_TAPPED,
	TRACK_EVENT_WHAT_TO_EXPECT_TAPPED,
} from "../../../lib/track/analyticsEvents";
import { getEventTypeColor } from "../../../lib/track/eventTypeColor";
import { getEventTypeIcon } from "../../../lib/track/eventTypeIcon";
import { formatRecurrence } from "../../../lib/track/formatRecurrence";
import { parseSyntheticEventId } from "../../../lib/track/parseSyntheticEventId";
import type { Event } from "../../../types/event";
import { BottomSheet } from "../../ui/BottomSheet";
import { DeleteEventConfirm } from "../AddEvent/DeleteEventConfirm";
import styles from "./EventDetailSheet.module.css";

const LOCALE_MAP = { en: enUS, es, fr, he, ru } as const;
const TITLE_ID = "event-detail-sheet-title";
const SWIPE_THRESHOLD = 80;

interface EventDetailSheetProps {
	isOpen: boolean;
	onClose: () => void;
	events: Event[]; // same-day list
	activeIndex: number;
	onIndexChange: (index: number) => void;
	/** Phase 37 D-18 — called when patient taps Edit (only for source='patient'). TrackPage opens AddEventSheet in mode='edit'. */
	onEdit?: (event: Event) => void;
	/** Phase 37 D-17 — optional notify-parent hook after a successful delete. */
	onDelete?: (event: Event) => void;
}

export function EventDetailSheet({
	isOpen,
	onClose,
	events,
	activeIndex,
	onIndexChange,
	onEdit,
	onDelete,
}: EventDetailSheetProps) {
	const { t, locale } = useClientTranslations("track");
	const dateFnsLocale = LOCALE_MAP[locale as keyof typeof LOCALE_MAP] ?? enUS;
	const isRtl = locale === "he";
	const navigate = useNavigate();
	const { triggerMessage } = useChatActions();
	const { logEvent } = useAnalytics();
	const [deleteOpen, setDeleteOpen] = useState(false);

	const event = events[activeIndex];

	// Close the delete dialog if the sheet itself closes (defensive — keeps UI consistent).
	useEffect(() => {
		if (!isOpen && deleteOpen) setDeleteOpen(false);
	}, [isOpen, deleteOpen]);

	// Reset to safe index if list shrinks (defensive — should not happen in P36)
	useEffect(() => {
		if (isOpen && activeIndex >= events.length && events.length > 0) {
			onIndexChange(0);
		}
	}, [isOpen, activeIndex, events.length, onIndexChange]);

	if (!event) {
		return (
			<BottomSheet
				isOpen={isOpen}
				onClose={onClose}
				ariaLabelledBy={TITLE_ID}
				panelHeight="tall"
			>
				<div />
			</BottomSheet>
		);
	}

	const visualCategory = getEventTypeColor(event.type);
	const synthetic = parseSyntheticEventId(event.id);
	const isRecurring = synthetic !== null;
	const TypeIcon = getEventTypeIcon(event.type);
	const eventTypeLabel = t(`eventType.${event.type}`);

	const dateObj = parseISO(event.dateTime);
	const dateTimeFormatted = t("event.dateTime", {
		date: format(dateObj, "PPPP", { locale: dateFnsLocale }),
		time: format(dateObj, "p", { locale: dateFnsLocale }),
	});

	const canPrev = activeIndex > 0;
	const canNext = activeIndex < events.length - 1;

	const goPrev = () => {
		if (canPrev) onIndexChange(activeIndex - 1);
	};
	const goNext = () => {
		if (canNext) onIndexChange(activeIndex + 1);
	};

	// D-09 + RESEARCH Pitfall 1: horizontal swipe with RTL inversion (preserved verbatim from Phase 36).
	// LTR: swipe-left → next, swipe-right → previous.
	// RTL: swipe-left → previous, swipe-right → next (mirrored).
	function handleSwipeEnd(
		_e: MouseEvent | TouchEvent | PointerEvent,
		info: PanInfo,
	) {
		const offset = info.offset.x;
		if (Math.abs(offset) < SWIPE_THRESHOLD) return;
		const swipedRight = offset > 0;
		// In LTR, swipe-right means "previous"; in RTL, the meaning flips.
		const wantPrev = isRtl ? !swipedRight : swipedRight;
		if (wantPrev && canPrev) {
			onIndexChange(activeIndex - 1);
		} else if (!wantPrev && canNext) {
			onIndexChange(activeIndex + 1);
		}
	}

	// D-07: What-to-Expect → React Router navigate (Capacitor-safe, per CLAUDE.md).
	// PHI-safe analytics payload — enums + boolean only (C-06).
	function handleWhatToExpect() {
		logEvent(TRACK_EVENT_WHAT_TO_EXPECT_TAPPED, undefined, {
			eventType: event.type,
			isRecurring,
		});
		navigate(`/education?topic=${event.type}`);
	}

	// D-08: Ask-Eve → seed per-type i18n template, trigger chat, navigate.
	// PHI contract (D-08 refinement, post-UAT 2026-05-02):
	//   • Analytics payload (logEvent) is strictly enum-only — no title/location/
	//     notes/doctorName/medicationName. Mechanically asserted by Suite 5 of
	//     pnpm test:event-sheet (balanced-paren extraction).
	//   • Chat seed (triggerMessage) MAY interpolate event.title only — user's
	//     own data flowing into user's own authenticated chat session. Other
	//     PHI fields (location/notes/doctorName/medicationName) stay blocked.
	// Reference pattern: FileCard.tsx:67-80, TimelineStepModalCustom.tsx:395-400.
	function handleAskEve() {
		logEvent(TRACK_EVENT_ASK_EVE_TAPPED, undefined, {
			eventType: event.type,
			isRecurring,
		});
		const seedMessage = t(`event.askEve.${event.type}.seedMessage`, {
			eventTitle: event.title,
		});
		triggerMessage(seedMessage, {
			eventType: event.type,
			isRecurring,
		});
		navigate("/chat");
	}

	const headerBorderClass = styles[`headerBorder--${visualCategory}`];
	const summaryCardClass = `${styles.summaryCard} ${styles[`summaryCard--${visualCategory}`]}`;
	const iconCircleClass = `${styles.iconCircle} ${styles[`iconCircle--${visualCategory}`]}`;
	const separatorClass = `${styles.summarySeparator} ${styles[`summarySeparator--${visualCategory}`]}`;
	const tipBulletClass = `${styles.tipBullet} ${styles[`tipBullet--${visualCategory}`]}`;
	const whatToExpectCardClass = `${styles.whatToExpectCard} ${styles[`whatToExpectCard--${visualCategory}`]}`;
	const whatToExpectIconClass = `${styles.whatToExpectIcon} ${styles[`iconCircle--${visualCategory}`]}`;

	const sheetHeader = (
		<div className={`${styles.headerRow} ${headerBorderClass}`}>
			<button
				type="button"
				className={styles.navBtn}
				aria-label={t("sheet.prevEvent")}
				onClick={goPrev}
				disabled={!canPrev}
				aria-disabled={!canPrev}
			>
				<ChevronLeftIcon className={styles.navIcon} />
			</button>
			<h2 className={styles.title} id={TITLE_ID}>
				{event.title}
			</h2>
			<button
				type="button"
				className={styles.navBtn}
				aria-label={t("sheet.nextEvent")}
				onClick={goNext}
				disabled={!canNext}
				aria-disabled={!canNext}
			>
				<ChevronRightIcon className={styles.navIcon} />
			</button>
			<button
				type="button"
				className={styles.closeBtn}
				aria-label={t("sheet.close")}
				onClick={onClose}
			>
				<XMarkIcon className={styles.closeIcon} />
			</button>
		</div>
	);

	const aboutTitle = t(`event.about.${event.type}.title`);
	const aboutBody = t(`event.about.${event.type}.body`);
	const tipsTitle = t(`event.tips.${event.type}.title`);
	// WR-08: i18next returns the key string itself when a path is missing —
	// guard against non-array / non-string-array shapes so a locale gap or
	// schema regression cannot crash the sheet at .map() time below.
	const tipsItemsRaw = t(`event.tips.${event.type}.items`, {
		returnObjects: true,
	});
	const tipsItems: string[] = Array.isArray(tipsItemsRaw)
		? tipsItemsRaw.filter((x): x is string => typeof x === "string")
		: [];
	const wteTitle = t(`event.whatToExpect.${event.type}.title`);
	const wteSubtext = t(`event.whatToExpect.${event.type}.subtext`);

	// Phase 37 D-17 / C-14 — footer renders ONLY when event.source === 'patient'.
	// Clinic-source events stay read-only (EVT-09 / era-api 403 CLINIC_EVENT_READONLY
	// at PUT/DELETE boundary as defence-in-depth).
	const sheetFooter =
		event.source === "patient" ? (
			<div className={styles.footerRow}>
				<button
					type="button"
					className={`${styles.footerBtn} ${styles.deleteBtn}`}
					onClick={() => setDeleteOpen(true)}
					aria-label={t("event.detail.footer.delete")}
				>
					<TrashIcon className={styles.footerIcon} aria-hidden="true" />
					<span>{t("event.detail.footer.delete")}</span>
				</button>
				<button
					type="button"
					className={`${styles.footerBtn} ${styles.editBtn}`}
					onClick={() => onEdit?.(event)}
					aria-label={t("event.detail.footer.edit")}
				>
					<PencilIcon className={styles.footerIcon} aria-hidden="true" />
					<span>{t("event.detail.footer.edit")}</span>
				</button>
			</div>
		) : undefined;

	return (
		<>
			<BottomSheet
				isOpen={isOpen}
				onClose={onClose}
				ariaLabelledBy={TITLE_ID}
				header={sheetHeader}
				footer={sheetFooter}
				panelHeight="tall"
			>
				<motion.div
					className={styles.body}
					drag="x"
					dragConstraints={{ left: 0, right: 0 }}
					dragElastic={0.1}
					onDragEnd={handleSwipeEnd}
				>
					{/* Summary card (D-03, D-04, D-10) */}
					<section className={summaryCardClass}>
						<div className={styles.summaryHeader}>
							<span
								className={iconCircleClass}
								aria-label={t("event.a11y.summaryIcon", { eventTypeLabel })}
								role="img"
							>
								<TypeIcon className={styles.iconGlyph} aria-hidden="true" />
							</span>
							<div className={styles.summaryTextStack}>
								<h3 className={styles.summaryTitle}>{event.title}</h3>
								<p className={styles.dateTime}>{dateTimeFormatted}</p>
								{isRecurring && event.frequencyRRULE && (
									<p className={styles.recurringBadge}>
										{t("event.recurringSeries", {
											rule: formatRecurrence(event.frequencyRRULE, t),
										})}
									</p>
								)}
							</div>
						</div>
						<div className={separatorClass} aria-hidden="true" />
						{event.location && (
							<div className={styles.locationRow}>
								<MapPinIcon
									className={styles.locationIcon}
									aria-hidden="true"
								/>
								<span className={styles.locationText}>{event.location}</span>
							</div>
						)}
					</section>

					{/* About this {type} (D-05) */}
					<section className={styles.aboutBlock}>
						<h3 className={styles.sectionTitle}>{aboutTitle}</h3>
						<p className={styles.aboutBody}>{aboutBody}</p>
					</section>

					{/* Preparation tips (D-05) */}
					<section className={styles.tipsBlock}>
						<h3 className={styles.sectionTitle}>{tipsTitle}</h3>
						<ul className={styles.tipsList}>
							{tipsItems.map((tip) => (
								<li key={tip} className={styles.tipItem}>
									<span className={tipBulletClass} aria-hidden="true" />
									<span className={styles.tipText}>{tip}</span>
								</li>
							))}
						</ul>
					</section>

					{/* What-to-Expect navigation card (D-06, D-07) */}
					<button
						type="button"
						className={whatToExpectCardClass}
						onClick={handleWhatToExpect}
						aria-label={t("event.a11y.whatToExpectCard", { eventTypeLabel })}
					>
						<span className={whatToExpectIconClass} aria-hidden="true">
							<PlayIcon className={styles.iconGlyph} />
						</span>
						<span className={styles.whatToExpectText}>
							<span className={styles.sectionTitle}>{wteTitle}</span>
							<span className={styles.whatToExpectSubtext}>{wteSubtext}</span>
						</span>
						<ChevronRightIcon
							className={styles.whatToExpectChevron}
							aria-hidden="true"
						/>
					</button>

					{/* Ask-Eve full-width pill (D-08) */}
					<button
						type="button"
						className={styles.askEveButton}
						onClick={handleAskEve}
						aria-label={t("event.a11y.askEveButton", { eventTypeLabel })}
					>
						<img
							src="/images/bottom-black-nav-chat.svg"
							alt=""
							className={styles.askEveIcon}
							aria-hidden="true"
						/>
						<span className={styles.askEveLabel}>
							{t("event.askEve.button")}
						</span>
					</button>
				</motion.div>
			</BottomSheet>
			{event.source === "patient" && (
				<DeleteEventConfirm
					isOpen={deleteOpen}
					event={event}
					onCancel={() => setDeleteOpen(false)}
					onSuccess={() => {
						setDeleteOpen(false);
						onClose();
						onDelete?.(event);
					}}
				/>
			)}
		</>
	);
}
